import { useEffect, useMemo, useRef } from "react";

export interface DebouncedCallback<T extends (...args: never[]) => void> {
  (...args: Parameters<T>): void;
  /** Clears any pending invocation. */
  cancel: () => void;
}

export function useDebouncedCallback<T extends (...args: never[]) => void>(
  callback: T,
  delay: number,
): DebouncedCallback<T> {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return useMemo(() => {
    const debounced = (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => callback(...args), delay);
    };
    const cancel = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    return Object.assign(debounced, { cancel });
  }, [callback, delay]);
}
